import { useAuthStore } from '../stores/authStore';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

export default function ProfilePage() {
  const { user } = useAuthStore();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const response = await api.get(`/users/${user?.id}`);
      return response.data.data;
    },
    enabled: !!user?.id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shark-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Profile Header */}
        <div className="bg-shark-800 rounded-xl p-8 mb-8">
          <div className="flex items-center space-x-6">
            <div className="w-24 h-24 bg-shark-600 rounded-full flex items-center justify-center text-4xl">
              {user?.displayName?.[0] || user?.username?.[0] || '?'}
            </div>
            <div>
              <h1 className="text-3xl font-bold">{user?.displayName || user?.username}</h1>
              <p className="text-gray-400">@{user?.username || 'Guest'}</p>
              {user?.isGuest && (
                <span className="inline-block mt-2 bg-yellow-600 text-sm px-2 py-1 rounded">
                  Guest Account
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {profile?.stats?.map((stat: { game_type: string; games_played: number; games_won: number; elo_rating: number }) => (
            <div key={stat.game_type} className="bg-shark-800 rounded-xl p-4">
              <h3 className="text-lg font-semibold capitalize mb-2">
                {stat.game_type.replace('_', ' ')}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-400">Games</p>
                  <p className="text-xl font-bold">{stat.games_played}</p>
                </div>
                <div>
                  <p className="text-gray-400">Wins</p>
                  <p className="text-xl font-bold">{stat.games_won}</p>
                </div>
                <div>
                  <p className="text-gray-400">Win Rate</p>
                  <p className="text-xl font-bold">
                    {stat.games_played > 0
                      ? Math.round((stat.games_won / stat.games_played) * 100)
                      : 0}
                    %
                  </p>
                </div>
                <div>
                  <p className="text-gray-400">ELO</p>
                  <p className="text-xl font-bold text-shark-400">{stat.elo_rating}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Games */}
        <div className="bg-shark-800 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4">Recent Games</h2>
          {profile?.recentGames?.length > 0 ? (
            <div className="space-y-3">
              {profile.recentGames.map((game: { id: string; game_type: string; is_winner: boolean; elo_change: number; started_at: string }) => (
                <div
                  key={game.id}
                  className="flex justify-between items-center bg-shark-700 rounded-lg p-3"
                >
                  <div>
                    <p className="font-semibold capitalize">{game.game_type.replace('_', ' ')}</p>
                    <p className="text-sm text-gray-400">
                      {new Date(game.started_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={game.is_winner ? 'text-green-400' : 'text-red-400'}>
                      {game.is_winner ? 'Won' : 'Lost'}
                    </p>
                    <p className="text-sm text-gray-400">
                      {game.elo_change > 0 ? '+' : ''}
                      {game.elo_change} ELO
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No games played yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
